import React, { useEffect } from "react";
import styled from "styled-components";
import { Button, StagingBadge, Tabs } from "@cinesys/ui/components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import CinesysLogo, { CINESYS_URL } from "../../components/CinesysLogo";
import AbLogo from "./AbLogo";
import { useVaultTools } from "./useVaultTools";
import {
  AB_TOP_TABS,
  AB_VAULT_INTERFACE_ID,
  AB_WORDMARK,
  BULK_MODE,
  PLACEHOLDERS_PATH,
  visibleTabs,
} from "./config";

/**
 * AB Vault shared header — brings this interface's chrome in line with `spotter`'s
 * `SpotterHeader`: same dark sticky bar, logo+wordmark lockup, org breadcrumb, tab row, and "Powered
 * by Cinesys" badge, just themed with the `--ab*` teal palette (`VaultThemeProvider`) instead of
 * NFL's `--spotter*` slate/amber. `CandidateAssignmentsScreen` (and any future AB Vault screen) renders
 * this itself, same as `FieldsScreen` renders `SpotterHeader` — replaces the old
 * per-screen `ScreenShell` header (AB logo + "Candidate Assignment" `<h2>`); that title now lives
 * as this header's own tab instead (`AB_TOP_TABS`), matching how NFL's page titles are tabs, not
 * in-body headings.
 *
 * Breadcrumb structure matches `SpotterHeader`'s exactly (prefix, "·" separator, org name, same
 * muted/strong color split) — except the prefix is `AB_WORDMARK` ("Vault") rather than a
 * "Customer Portal" product label, and the lockup next to `AbLogo` carries no separate wordmark
 * text (unlike `SpotterHeader`'s `SpotterMark` + "Spotter" `Brand`) since "Vault" now lives in the
 * breadcrumb instead.
 *
 * Tab identity (browser tab title): set here, not in `VaultThemeProvider` or `public/index.html` —
 * same reasoning as `SpotterHeader`'s `useSpotterTabIdentity`: this app's base document is shared by
 * every customer interface, so a global title there would brand-leak. Restores the previous title on
 * unmount. Title is "Vault" (`AB_VAULT_TITLE`), same string as `AB_WORDMARK` — no longer the company
 * name, see `config.ts`.
 *
 * The favicon is deliberately NOT part of that: every interface now shares the one CineSys mark from
 * `public/index.html`, so this no longer mirrors vault.ab21.org's own icon.
 */
const Header = styled.header`
  /* Shared \`--dark090\` (not a one-off \`--ab*\` hex) — Spotter's own header-bar tone (nflSlate),
   * one step lighter than the page canvas underneath it (\`CustomerLayout\`'s \`--dark060\`/nflPaper)
   * but a separate token from \`--dark100\`'s card-surface role (nflSurface), same as
   * \`SpotterHeader\`'s own \`--spotterSlate\` background and the manager app's \`Header.tsx\`. Ties this header
   * into the shared design system rather than a near-invisible custom shade indistinguishable from
   * the body. */
  background: var(--dark090);
  color: #fff;
  padding: 13px 22px 0;
  position: sticky;
  top: 0;
  z-index: 30;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: nowrap;
`;

const Lockup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

/** `min-width: 0` + `overflow: hidden`/ellipsis so a long org name truncates instead of forcing
 *  `Top` to wrap or overflow — same trick as `SpotterHeader`'s `Crumb`. */
const Crumb = styled.div`
  font-size: 15px;
  color: var(--headerCrumbMuted);
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  span {
    color: var(--headerCrumbStrong);
    font-weight: 600;
  }
`;

/** Placeholder-width skeleton shown in place of the org name until it has actually loaded — same
 *  "never show a fake name, never show a bare separator" reasoning as `SpotterHeader`'s
 *  `CrumbSkeleton`.
 *
 * Round 18 (Addison): the SAME treatment now covers everything else in this header that's
 * identifying — `LogoSkeleton` stands in for `AbLogo`, a second `CrumbSkeleton` for the
 * `AB_WORDMARK` ("Vault") text, and `TabsSkeleton` for the nav tab labels — see `SpotterHeader`'s
 * equivalent doc comment. All key off the same `access.status === "granted"` boolean. */
const CrumbSkeleton = styled.span`
  display: inline-block;
  width: 90px;
  height: 0.85em;
  border-radius: 3px;
  background: var(--abField);
  vertical-align: -0.1em;
`;

const WordmarkSkeleton = styled.span`
  display: inline-block;
  width: 40px;
  height: 0.85em;
  border-radius: 3px;
  background: var(--abField);
  vertical-align: -0.1em;
`;

const LogoSkeleton = styled.span`
  display: inline-block;
  width: 93px;
  height: 30px;
  border-radius: 4px;
  background: var(--abField);
`;

const TabPillSkeleton = styled.span`
  display: inline-block;
  width: 140px;
  height: 30px;
  border-radius: 6px;
  background: var(--abField);
  flex-shrink: 0;
`;

const TabsSkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

/** Same tab COUNT as `AB_TOP_TABS`, fixed width — see `SpotterHeader`'s `SpotterTabsSkeleton`
 *  doc comment for why the width is deliberately NOT derived from the real label. */
const AbTabsSkeleton = () => (
  <TabsSkeletonRow aria-hidden="true">
    {AB_TOP_TABS.map((tab) => (
      <TabPillSkeleton key={tab.key} />
    ))}
  </TabsSkeletonRow>
);

const Spacer = styled.div`
  flex: 1;
`;

const PoweredBy = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;
  opacity: 0.85;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }
`;

const PoweredByLabel = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--abLabel);
  white-space: nowrap;
`;

/** Re-skins `@cinesys/ui`'s `Tabs` to the `--ab*` palette by overriding the same CSS custom
 *  properties it reads its colors from — same CSS-var-override technique `SpotterHeader` used for
 *  its own tab bar re-skin before that one moved to a carousel-based implementation. Active-tab
 *  color is left as the ambient `--blue300` (not overridden to `--abAccent`) — `VaultThemeProvider`
 *  already re-hues the shared blue ramp to the wordmark's own logo-blue, so this falls through to
 *  that instead of introducing a second, slightly different accent hue. */
const HeaderTabs = styled(Tabs)`
  --dark200: transparent;
  --dark100: transparent;
  --gray300: var(--abLabel);
  width: auto;
  margin-right: 0;
  padding: 0;
  border-bottom: none;

  button,
  a {
    font-size: 14px;
    font-weight: 600;
    padding: 8px 14px;
    border-bottom-width: 2.5px;
  }
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
`;

const AB_VAULT_TITLE = "Vault";

/** See "Tab identity" in the doc comment above for why this lives here and not in
 *  `VaultThemeProvider`/`public/index.html`. Round 18: takes `ready` (`useInterfaceAccess`'s
 *  "granted" check) — see `SpotterHeader`'s equivalent `useSpotterTabIdentity` doc comment. */
const useVaultTabIdentity = (ready: boolean) => {
  useEffect(() => {
    if (!ready) {
      return undefined;
    }
    const previousTitle = document.title;
    document.title = AB_VAULT_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, [ready]);
};

const ModeRow = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 20px;
  display: flex;
  gap: 10px;
`;

/** Single / Bulk toggle at the top of the Placeholders page; scrolls with the content. Navigation
 *  only — access gating is the route/API, not this toggle. */
export const AbModeNav = () => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  if (!pathname.startsWith(PLACEHOLDERS_PATH)) {
    return null;
  }
  const bulk = params.get("mode") === BULK_MODE;

  return (
    <ModeRow>
      <Button
        type="default"
        label="Single"
        link={PLACEHOLDERS_PATH}
        selected={!bulk}
      />
      <Button
        type="default"
        label="Bulk"
        link={`${PLACEHOLDERS_PATH}?mode=${BULK_MODE}`}
        selected={bulk}
      />
    </ModeRow>
  );
};

/** Only renders header CHROME — the "denied" content notice is each mount site's own job, same
 *  split as `SpotterHeader` (see its doc comment) — `CandidateAssignmentsScreen`'s `ScreenShell`
 *  already gates its content area on `useInterfaceAccess` before rendering this. */
const AbHeader = () => {
  const access = useInterfaceAccess(AB_VAULT_INTERFACE_ID);
  const ready = access.status === "granted";
  const orgName = access.status === "granted" ? access.customerName : undefined;
  useVaultTabIdentity(ready);
  const vaultTools = useVaultTools();
  const tabsReady = ready && vaultTools.status !== "pending";
  const tabs = visibleTabs(
    vaultTools.status === "ready" ? vaultTools.tools : []
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const activeTab = tabs.find((t) => pathname.startsWith(t.key));

  return (
    <Header>
      <Top>
        <Lockup>
          {ready ? <AbLogo height={30} /> : <LogoSkeleton aria-hidden="true" />}
          <StagingBadge />
        </Lockup>
        <Crumb>
          {ready ? (
            <>
              {AB_WORDMARK} &nbsp;·&nbsp; <span>{orgName}</span>
            </>
          ) : (
            <>
              <WordmarkSkeleton aria-hidden="true" /> &nbsp;·&nbsp;{" "}
              <CrumbSkeleton aria-hidden="true" />
            </>
          )}
        </Crumb>
        <Spacer />
        <PoweredBy href={CINESYS_URL} target="_blank" rel="noopener noreferrer">
          <PoweredByLabel>Powered by</PoweredByLabel>
          <CinesysLogo height={34} />
        </PoweredBy>
      </Top>
      <TabsRow>
        {tabsReady ? (
          <HeaderTabs
            mode="wrap"
            value={activeTab?.key}
            onChange={(key) => navigate(key)}
            tabs={tabs.map((t) => ({ key: t.key, label: t.label }))}
          />
        ) : (
          <AbTabsSkeleton />
        )}
      </TabsRow>
    </Header>
  );
};

export default AbHeader;
